import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaGlobe, FaAngleDown, FaAngleUp, FaCheck } from 'react-icons/fa';

// Configuration des langues avec codes pays pour les drapeaux
const languages = [
  { code: 'fr', name: 'Français', countryCode: 'fr', nativeName: 'Français' },
  { code: 'en', name: 'English', countryCode: 'gb', nativeName: 'English' },
  { code: 'ru', name: 'Русский', countryCode: 'ru', nativeName: 'Русский' },
  { code: 'ar', name: 'العربية', countryCode: 'sa', nativeName: 'العربية', rtl: true },
];

// Composant pour afficher les drapeaux
const FlagImage = ({ countryCode, alt, className = "" }) => {
  const [hasError, setHasError] = useState(false);
  
  if (hasError) {
    return <FaGlobe className={`text-slate-400 ${className}`} />;
  }
  
  return (
    <img
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
      alt={alt}
      className={`w-5 h-auto rounded-sm ${className}`}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};

const LanguageSwitcher = ({ variant = 'dropdown' }) => {
  const { i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsDropdownOpen(false);
    
    // Sauvegarder dans localStorage pour persistance
    localStorage.setItem('i18nextLng', lng);
  };

  // Mettre à jour l'attribut lang et dir de la balise <html> lorsque la langue change
  useEffect(() => {
    const langCode = i18n.language.split('-')[0];
    const currentLang = languages.find(l => l.code === langCode);
    
    document.documentElement.lang = langCode;
    
    // Gérer la direction RTL pour l'arabe
    if (currentLang?.rtl) {
      document.documentElement.dir = 'rtl';
      document.body.classList.add('rtl');
    } else {
      document.documentElement.dir = 'ltr';
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);

  // Gérer les clics en dehors du dropdown pour le fermer
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fermer avec la touche Escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);
  
  const currentLanguage = languages.find(lang => i18n.language.startsWith(lang.code)) || languages[0];

  // Variante "buttons" : affiche tous les drapeaux côte à côte
  if (variant === 'buttons') {
    return (
      <div className="flex items-center gap-1">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            title={lang.nativeName}
            className={`p-1.5 rounded-md transition-all duration-200
                        ${i18n.language.startsWith(lang.code) 
                          ? 'bg-sky-500/30 ring-2 ring-sky-500/50 scale-110' 
                          : 'hover:bg-slate-700/50 opacity-70 hover:opacity-100'}`}
            aria-label={`Changer la langue en ${lang.name}`}
            aria-pressed={i18n.language.startsWith(lang.code)}
          >
            <FlagImage countryCode={lang.countryCode} alt={lang.name} />
          </button>
        ))}
      </div>
    );
  }

  // Variante "dropdown" (par défaut) : menu déroulant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 
                   text-slate-300 hover:bg-slate-700/50 hover:text-white
                   focus:outline-none focus:ring-2 focus:ring-sky-500/50"
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
        aria-label="Sélectionner la langue"
      >
        <FlagImage countryCode={currentLanguage.countryCode} alt={currentLanguage.name} />
        <span className="hidden sm:inline">{currentLanguage.code.toUpperCase()}</span>
        {isDropdownOpen ? <FaAngleUp className="text-xs" /> : <FaAngleDown className="text-xs" />}
      </button>

      {isDropdownOpen && (
        <div
          className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-xl 
                     border border-slate-700/50 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          role="listbox"
          aria-label="Langues disponibles"
        >
          <div className="py-1">
            {languages.map((lang) => {
              const isSelected = i18n.language.startsWith(lang.code);
              return (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  title={lang.name}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150
                              ${isSelected 
                                ? 'bg-sky-600/20 text-sky-300' 
                                : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'}`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <FlagImage countryCode={lang.countryCode} alt={lang.name} />
                  <span className="flex-grow text-left">{lang.nativeName}</span>
                  {isSelected && <FaCheck className="text-sky-400 text-xs" />}
                </button>
              );
            })}
          </div>
          
          {/* Info pour traduction navigateur */}
          <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-900/50">
            <p className="text-[10px] text-slate-500 text-center">
              💡 Utilisez la traduction de votre navigateur pour d'autres langues
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
